import React, { useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { navigate } from 'gatsby'

import { sendRequest } from '../../services/serverless'

const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/
const requiredMgs = 'This field is required'

const FILE_SIZE = 10000000 // 10MB is the maximum size allowed
const SUPPORTED_FORMATS = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

// Fields that can be placed beside a label without looking strange
const VISUALLY_SMALL_FIELDS = ['Short text', 'File', 'Email', 'Phone', 'URL']

const getFieldOptions = field_options =>
  field_options.raw.map(option => option.text)

const template = (text, data) => {
  const keys = (text.match(/\${.*?}/g) ?? []).map(key =>
    key.substring(2, key.length - 1)
  )
  const transformedText = keys.reduce(
    (text, key) => text.replace('${' + key + '}', data[key]),
    text
  )
  return transformedText
}

const FormField = ({ id, templateData, setFieldValue, specification }) => {
  const {
    long_field_label,
    field_name,
    field_type,
    required,
    placeholder,
    field_options,
    use_templating,
  } = specification

  // Place field to the left of the input when the field is visually small
  const fieldWrapperClass = `form-field${
    long_field_label.html === '' && VISUALLY_SMALL_FIELDS.includes(field_type)
      ? ' grid grid--2col-1-2'
      : ''
  }`

  // Labels may be mandatory
  const labelClass = `visible${required === 'Yes' ? ' mandatory' : ''}`

  // Populate template field labels when templating is enabled
  const templated_long_field_label =
    use_templating === 'Yes'
      ? template(long_field_label.html, templateData)
      : long_field_label.html
  const templated_field_name =
    use_templating === 'Yes' ? template(field_name, templateData) : field_name

  // Props for label of field, to be spread inside JSX tag
  const labelContent = long_field_label.text
    ? { dangerouslySetInnerHTML: { __html: templated_long_field_label } }
    : { children: templated_field_name }

  const errorMessage = (
    <ErrorMessage
      name={id}
      render={msg => <div className="form-error-message">{msg}</div>}
    />
  )

  switch (field_type) {
    case 'Email':
    case 'Phone':
    case 'URL':
    case 'Short text': {
      return (
        <div className={fieldWrapperClass}>
          <label htmlFor={id} className={labelClass} {...labelContent} />
          <div>
            <Field type="text" id={id} name={id} placeholder={placeholder} />
            {errorMessage}
          </div>
        </div>
      )
    }
    case 'Long text': {
      return (
        <div className={fieldWrapperClass}>
          <label htmlFor={id} className={labelClass} {...labelContent} />
          <div>
            <Field as="textarea" id={id} name={id} placeholder={placeholder} />
            {errorMessage}
          </div>
        </div>
      )
    }
    case 'File': {
      return (
        <div className={fieldWrapperClass}>
          <label htmlFor={id} className={labelClass} {...labelContent} />
          <div>
            <Field
              type="file"
              id={id}
              name={id}
              placeholder={placeholder}
              accept={SUPPORTED_FORMATS.join(',')}
              value={undefined}
              onChange={event => setFieldValue(id, event.target?.files[0])}
            />
            {errorMessage}
          </div>
        </div>
      )
    }
    case 'Checkbox': {
      return (
        <div className={fieldWrapperClass}>
          <div {...labelContent} />
          <div className="group-option--more-top">
            <Field type="checkbox" id={id} name={id} />
            <label htmlFor={id} className={labelClass}>
              {getFieldOptions(field_options)[0] ?? field_name}
            </label>
          </div>
          {errorMessage}
        </div>
      )
    }
    case 'Radio': {
      return (
        <div className={fieldWrapperClass}>
          <div id={`${id} label`} className={labelClass} {...labelContent} />
          {errorMessage}
          <div role="group" aria-labelledby={`${id} label`}>
            {getFieldOptions(field_options).map(option => (
              <div key={option} className="group-option">
                <Field
                  type="radio"
                  id={`${id} ${option}`}
                  name={id}
                  value={option}
                />
                <label htmlFor={`${id} ${option}`} className="visible">
                  {option}
                </label>
              </div>
            ))}
          </div>
        </div>
      )
    }
    default: {
      // Don't render unexpected types
      return null
    }
  }
}

const makeId = (section, item) =>
  `${section.primary.section_title} ${item.field_name}`

const makeValidation = item => {
  const { field_type, field_options, required } = item

  let validation = Yup

  switch (field_type) {
    case 'Short text': {
      validation = validation.string()
      break
    }
    case 'Long text': {
      validation = validation.string()
      break
    }
    case 'File': {
      validation = validation
        .mixed()
        .test(
          'fileSize',
          'Resume file size is too large',
          value => value === undefined || value.size <= FILE_SIZE
        )
        .test(
          'fileType',
          'Unsupported file format for resume',
          value => value === undefined || SUPPORTED_FORMATS.includes(value.type)
        )
      break
    }
    case 'Email': {
      validation = validation.string().email('Invalid email')
      break
    }
    case 'Phone': {
      validation = validation
        .string()
        .matches(phoneRegExp, 'Phone number is not valid')
      break
    }
    case 'URL': {
      validation = validation.string()
      break
    }
    case 'Checkbox': {
      validation = validation
        .bool()
        .oneOf([...(required === 'Yes' ? [] : [false]), true], requiredMgs)
      break
    }
    case 'Radio': {
      validation = validation
        .mixed()
        .oneOf(
          [
            ...(required === 'Yes' ? [] : ['']),
            ...getFieldOptions(field_options),
          ],
          requiredMgs
        )
      break
    }
    default: {
      // Don't validate unexpected types
      return Yup.mixed()
    }
  }

  if (required === 'Yes') {
    validation = validation.required(requiredMgs)
  }

  return validation
}

const toBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64String = reader.result
        .replace('data:', '')
        .replace(/^.+,/, '')
      resolve(base64String)
    }
    reader.readAsDataURL(file)
    reader.onerror = error => reject(error)
  })
}

const getValue = async (values, section, item) => {
  const value = values[makeId(section, item)]
  switch (item.field_type) {
    case 'Short text':
    case 'Long text':
    case 'Email':
    case 'Phone':
    case 'URL':
    case 'Radio':
    case 'Checkbox':
      return value
    case 'File':
      return value === ''
        ? value
        : {
            file: await toBase64(value),
            name: value.name,
            type: value.type,
          }
    default:
      // Don't provide data for unexpected types
      return ''
  }
}

const ApplyForm = ({
  jobTitle,
  jobSlug,
  jobId,
  jobCountry,
  jobCity,
  formSpecification,
}) => {
  const [formSubmitMessage, setFormSubmitMessage] = useState('')

  const formSections = formSpecification.body

  // Flat object of form field IDs and associated Prismic specifications
  const formFields = formSections.reduce(
    (acc, section) => ({
      ...acc,
      ...section.items.reduce(
        (acc, item) => ({ ...acc, [makeId(section, item)]: item }),
        {}
      ),
    }),
    {}
  )

  // All fields start as blank
  const initialValues = Object.keys(formFields).reduce(
    (acc, key) => ({ ...acc, [key]: '' }),
    {}
  )

  // Provide validation for each form field
  const validationSchema = Yup.object().shape(
    Object.entries(formFields).reduce(
      (acc, [key, item]) => ({ ...acc, [key]: makeValidation(item) }),
      {}
    )
  )

  const onHandleSubmit = async (values, { setSubmitting }) => {
    try {
      // type Record<string, Record<string, string | { file: string; type: string; name: string }>>
      const formValues = {}
      for (const section of formSections) {
        const sectionTitle = section.primary.section_title
        formValues[sectionTitle] = {}
        for (const item of section.items) {
          formValues[sectionTitle][item.field_name] = await getValue(
            values,
            section,
            item
          )
        }
      }

      await sendRequest(
        {
          jobTitle,
          jobId,
          formValues,
        },
        'uploadCV'
      )
      navigate(`/careers/${jobSlug}/thanks`, { replace: true })
    } catch (error) {
      setFormSubmitMessage(error.message)
    }

    setSubmitting(false)
  }

  // Template data is passed to fields that use the use_templating feature.
  // Don't put sensitive information here. All data in this object can be shown
  // to the user.
  const templateData = { country: jobCountry, city: jobCity }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onHandleSubmit}
    >
      {({ isSubmitting, setFieldValue, errors, submitCount }) => (
        <Form className="apply-form">
          {formSections.map(section => (
            <React.Fragment key={section.primary.section_title}>
              <p className="t-section-heading--less-bottom">
                {section.primary.section_title}
              </p>
              {section.items.map(item => (
                <FormField
                  key={item.field_name}
                  id={makeId(section, item)}
                  specification={item}
                  templateData={templateData}
                  setFieldValue={setFieldValue}
                />
              ))}
            </React.Fragment>
          ))}

          {formSubmitMessage && <p>{formSubmitMessage}</p>}
          {submitCount > 0 && Object.keys(errors).length > 0 && (
            <p className="form-error-color">
              One or more fields are missing or incorrect.
            </p>
          )}

          <div className="submit-btn">
            <button
              type="submit"
              className="button button--outline"
              disabled={isSubmitting}
            >
              SUBMIT APPLICATION
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default ApplyForm
